import { KIND, SENDING_TIME_FORMAT } from './templateFormShape';

/**
 * The submit payload, derived from the form store.
 *
 * Extracted from the drawer so the rules below can be read — and tested —
 * without mounting a three-pane editor to reach the Save button. Every one of
 * them exists because of a bug the shape caused:
 *
 * - `kind` is UI-only and `journey_hash_id` is the form's name for what a create
 *   sends as `journey_id`, so neither may go out as is.
 * - `sending_time` is marketing-only. The field is not rendered for
 *   transactional templates, but the store may still hold a value (an old
 *   record, or the kind toggled while editing) — it must never be submitted for
 *   those.
 * - The sender override is opt-in and the two fields *are* how the API stores
 *   it, so a toggle that is off has to be sent as explicit nulls: not whatever
 *   the store still holds, and not `undefined`, which would leave an override
 *   the user just switched off in place on the server.
 * - `journey_id` falls back to `scopeTarget` rather than being omitted. The
 *   field is only filled for a trigger-preselected transactional create, so the
 *   generic "new template" and every marketing one used to send nothing here
 *   and lean on the API's default lead source — which an account may not have.
 *   Naming the target explicitly is what keeps those paths from producing a
 *   template nothing links.
 */
export interface TemplatePayloadInput {
  /** `form.getFieldsValue(true)` — every value in the store, not just the mounted ones. */
  values: Record<string, any>;
  isEdit: boolean;
  /** The journey a create links to when the form did not name one. */
  scopeTarget: string | null;
}

export interface TemplatePayloadResult {
  /** What an update (or a create, before `journey_id`) sends. */
  payload: Record<string, any>;
  /** What a create sends: `payload` plus the resolved `journey_id`. */
  createPayload: Record<string, any>;
  /** The subset a draft save is allowed to stage. */
  draftPayload: Record<string, any>;
}

export function buildTemplatePayload({
  values: allValues,
  isEdit,
  scopeTarget,
}: TemplatePayloadInput): TemplatePayloadResult {
  const {
    kind,
    journey_hash_id: scopeJourneyHashId,
    sender_override: senderOverride,
    ...values
  } = allValues;

  const sendingTime =
    kind === KIND.MARKETING && values.sending_time
      ? values.sending_time.format(SENDING_TIME_FORMAT)
      : undefined;

  const senderFrom = senderOverride
    ? {
        sender_from_name: values.sender_from_name || null,
        sender_from_email: values.sender_from_email || null,
      }
    : { sender_from_name: null, sender_from_email: null };

  const payload = {
    ...values,
    email_type: kind === KIND.MARKETING ? '' : values.email_type,
    sending_time: sendingTime,
    ...senderFrom,
  };

  const createTarget = scopeJourneyHashId ?? scopeTarget;

  return {
    payload,
    createPayload:
      !isEdit && createTarget
        ? { ...payload, journey_id: createTarget }
        : payload,
    /**
     * A draft carries only the tracked fields the history row can hold. Name,
     * type and layout are not draftable and are simply absent — changing those
     * stays a live edit.
     */
    draftPayload: {
      subject: values.subject,
      pretext: values.pretext,
      body: values.body,
      design_document: values.design_document,
      sending_time: sendingTime,
      ...senderFrom,
    },
  };
}
